import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap, shareReplay } from 'rxjs/operators';
import { CommunicationManagerService } from './communication-manager.service';

export interface EnumData {
  mediaType: string[];
  usageMedium: string[];
  regions: string[];
}

export interface EnumState {
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  data: EnumData | null;
}

@Injectable({
  providedIn: 'root',
})
export class EnumManagerService {
  private enumStateSubject = new BehaviorSubject<EnumState>({
    isLoading: false,
    isLoaded: false,
    error: null,
    data: null,
  });

  private enumDataCache: EnumData | null = null;
  private lastLoadTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(private cm: CommunicationManagerService) {}

  /**
   * Get the current enum state as an observable
   */
  getEnumState(): Observable<EnumState> {
    return this.enumStateSubject.asObservable().pipe(
      shareReplay(1)
    );
  }

  /**
   * Get the current enum data
   */
  getEnumData(): EnumData | null {
    return this.enumStateSubject.value.data;
  }

  /**
   * Check if enums are currently loading
   */
  isLoading(): boolean {
    return this.enumStateSubject.value.isLoading;
  }

  /**
   * Check if enums are loaded
   */
  isLoaded(): boolean {
    return this.enumStateSubject.value.isLoaded;
  }

  /**
   * Check if there's an error
   */
  hasError(): boolean {
    return this.enumStateSubject.value.error !== null;
  }

  /**
   * Get the current error message
   */
  getError(): string | null {
    return this.enumStateSubject.value.error;
  }

  /**
   * Load enums from the server
   */
  loadEnums(): Observable<EnumData> {
    const currentTime = Date.now();
    
    // Check if we have cached data and it's still valid
    if (this.enumDataCache && (currentTime - this.lastLoadTime) < this.CACHE_DURATION) {
      return of(this.enumDataCache);
    }

    // Set loading state
    this.updateState({
      isLoading: true,
      isLoaded: false,
      error: null,
      data: null,
    });

    return this.cm.get('enum').pipe(
      tap((enums: any) => {
        const enumData: EnumData = {
          mediaType: enums['mediaType'] || [],
          usageMedium: enums['usageMedium'] || [],
          regions: enums['regions'] || ['Japan', 'Asia', 'Europe', 'America', 'Global', 'Specific'],
        };

        // Cache the data
        this.enumDataCache = enumData;
        this.lastLoadTime = currentTime;

        // Update state with success
        this.updateState({
          isLoading: false,
          isLoaded: true,
          error: null,
          data: enumData,
        });
      }),
      catchError((error) => {
        const errorMessage = error?.message || 'Failed to load enum data';
        
        // Update state with error
        this.updateState({
          isLoading: false,
          isLoaded: false,
          error: errorMessage,
          data: null,
        });

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Force refresh enums (ignores cache)
   */
  refreshEnums(): Observable<EnumData> {
    // Clear cache
    this.enumDataCache = null;
    this.lastLoadTime = 0;
    
    return this.loadEnums();
  }

  /**
   * Clear the enum cache
   */
  clearCache(): void {
    this.enumDataCache = null;
    this.lastLoadTime = 0;
  }

  /**
   * Get specific enum values
   */
  getMediaTypes(): string[] {
    return this.enumStateSubject.value.data?.mediaType || [];
  }

  getUsageMediums(): string[] {
    return this.enumStateSubject.value.data?.usageMedium || [];
  }

  getRegions(): string[] {
    return this.enumStateSubject.value.data?.regions || [];
  }

  /**
   * Validate if all required enums are loaded
   */
  validateEnums(): boolean {
    const data = this.enumStateSubject.value.data;
    return !!(data?.mediaType?.length && data?.usageMedium?.length);
  }

  /**
   * Update the internal state
   */
  private updateState(newState: Partial<EnumState>): void {
    const currentState = this.enumStateSubject.value;
    this.enumStateSubject.next({
      ...currentState,
      ...newState,
    });
  }
} 